<template>
  <div>
    <DxForm ref="formInstance" :form-data="model" label-location="top" :show-colon-after-label="false"
      :visible="model != null" validation-group="validacionClienteInmobiliario">
      <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

      <DxSimpleItem :col-span="6" :visible="!props.isEdit">
        <span class="dx-field-item-label-text text-success">
            {{ $t('clienteInmobiliario.form.inmueble') }}
        </span>
      </DxSimpleItem>

      <DxSimpleItem :col-span="6" :visible="!props.isEdit">
        <DxDropDownBox
          v-model:value="selectedInmuebleId"
          :data-source="inmueblesDataSource"
          display-expr="Direccion"
          :value-expr="'Id'"
          :show-clear-button="true"
          :open-on-field-click="true"
          :drop-down-options="{ width: 800, height: 400 }"
          v-model:opened="gridBoxOpened" 
        >
          <DxValidator ref="validatorInmueble" validation-group="validacionInmueble">
            <DxRequiredRule :message="$t('clienteInmobiliario.validations.inmuebleRequerido')" />
          </DxValidator>
          <template #content>
            <InmueblesGridSelector 
              v-model="selectedInmuebleId"
              @inmueble-selected="onInmuebleSelected" 
            />
          </template>
        </DxDropDownBox>
      </DxSimpleItem>

      <DxGroupItem :col-count="6" :col-span="6" :visible="!props.isEdit && selectedInmuebleId != null">
        <DxSimpleItem :col-span="6" :visible="!props.isEdit">
          <span class="dx-field-item-label-text text-success">
              {{ $t('clienteInmobiliario.form.datosRelacion') }}
          </span>
        </DxSimpleItem>
        <DxSimpleItem :col-span="6" :visible="!props.isEdit">
          <DxRadioGroup
            v-model="tipoRelacion"
            :items="tiposRelacion"
            value-expr="value"
            display-expr="text"
            layout="horizontal"
          />
        </DxSimpleItem>
        <DxSimpleItem data-field="FechaInicio" editor-type="dxDateBox" :col-span="3" >
          <DxLabel :text="$t('clienteInmobiliario.fechaInicio')" />
          <DxDateBox v-model="model.FechaInicio" validation-message-mode="always" :show-clear-button="true">
            <DxValidator ref="validatorFechaInicio" validation-group="validacionFechaInicio">
              <DxCustomRule
                :message="$t('clienteInmobiliario.validations.fechaInicioInferior')"
                :validation-callback="validateFechaInicio"
                :reevaluate="true"
              />
            </DxValidator>
          </DxDateBox>
        </DxSimpleItem>
        <DxSimpleItem data-field="FechaFin" editor-type="dxDateBox" :col-span="3">
          <DxLabel :text="$t('clienteInmobiliario.fechaFin')" />
          <DxDateBox v-model="model.FechaFin" validation-message-mode="always" :show-clear-button="true">
            <DxValidator ref="validatorFechaFin" validation-group="validacionFechaFin">
              <DxCustomRule
                :message="$t('clienteInmobiliario.validations.fechaFinSuperior')"
                :validation-callback="validateFechaFin"
                :reevaluate="true"
              />
            </DxValidator>
          </DxDateBox>
        </DxSimpleItem>
      </DxGroupItem>
    </DxForm>
    
    <DatosClienteInmobiliarioForm 
      ref="formCliente" 
      :is-embedded="true" 
      @cliente-saved="onClienteSaved" 
      :id-cliente-inmobiliario="idClienteInmobiliario"
      :key="idClienteInmobiliario"
    />

  </div>
</template>

<script setup lang="ts">
import { ref, onMounted,  getCurrentInstance, watch } from 'vue';
import { DxForm, DxLabel, DxColCountByScreen, DxCustomRule, DxSimpleItem, DxGroupItem, DxRequiredRule } from "devextreme-vue/form";
import { DxDateBox } from "devextreme-vue";
import { useApi } from '@api/integration';
import { TipoRelacionClienteInmobiliarioEnum } from '@api/api';
import validationEngine from "devextreme/ui/validation_engine";
import { useIntl } from '@services/i18n';
import { AddRelacionClienteInmobiliarioInmueble } from '@/components/relacion-cliente-inmobiliario/models/AddRelacionClienteInmobiliario';
import { EditRelacionClienteInmobiliarioInmueble } from '@/components/relacion-cliente-inmobiliario/models/EditRelacionClienteInmobiliario';
import DatosClienteInmobiliarioForm from '@/components/clientes-inmobiliarios/DatosClienteInmobiliarioForm.vue';
import { messageBoxResult } from '@components/dialogs/message-box';
import { useModalBuilder, type ToolbarReference } from '@helpers/modal-building-helper';
import { type DxButton } from 'devextreme-vue';
import DxValidator from 'devextreme-vue/validator';
import { DxRadioGroup, DxDropDownBox } from 'devextreme-vue';
import InmueblesGridSelector from '@/components/inmuebles/InmueblesGridSelector.vue';
import { toastError } from '@/.shared/services/notification-service';

export interface Props {
  isEdit: boolean;
  idClienteInmobiliario?: string;
}

const props = defineProps<Props>();
const tipoRelacion = ref<TipoRelacionClienteInmobiliarioEnum>(TipoRelacionClienteInmobiliarioEnum.Propietario);
const model = ref<AddRelacionClienteInmobiliarioInmueble | EditRelacionClienteInmobiliarioInmueble>(new AddRelacionClienteInmobiliarioInmueble({TipoRelacion: tipoRelacion.value}));
const api = useApi();
const intl = useIntl();
const formCliente = ref<InstanceType<typeof DatosClienteInmobiliarioForm>>();
const validatorFechaInicio = ref<DxValidator>();
const validatorFechaFin = ref<DxValidator>();
const validatorInmueble = ref<DxValidator>();

const formInstance = ref<DxForm>();

const selectedInmuebleId = ref<string | undefined>(undefined);
const inmueblesDataSource = ref<any[]>([]);
const tiposRelacion = [
  { text: intl.i18n.t('clienteInmobiliario.ui.inquilino'), value: TipoRelacionClienteInmobiliarioEnum.Inquilino },
  { text: intl.i18n.t('clienteInmobiliario.ui.propietario'), value: TipoRelacionClienteInmobiliarioEnum.Propietario }
];
const gridBoxOpened = ref(false);

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!)
  configureButtons(modal);
});

function configureButtons(modal: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  ({ okButton, cancelButton } = modal.standardButtons.addOkCancel());
  okButton.update(w => w.text = `${intl.i18n.t("shared.ui.buttons.save")}`);
  okButton.update(w => w.onClick = async () => {
    saveOrEdit().then((value) => {
      if (value)
        modal.close(messageBoxResult.ok)
    });
  });
  cancelButton.update(w => w.onClick = () => modal.close(messageBoxResult.cancel));
}

const isValid = () => {
  let valDatosCliente = formCliente.value?.isValid();

  if (!props.isEdit){
    let valInmueble = validationEngine.validateGroup("validacionInmueble").isValid;
    let valFechas = valInmueble && validationEngine.validateGroup("validacionFechaInicio").isValid && validationEngine.validateGroup("validacionFechaFin").isValid;
    return valDatosCliente && valInmueble && valFechas;
  } else {
    return valDatosCliente;
  }
};

const onClienteSaved = (idCliente: string) => {
  if (!props.isEdit) {
    (model.value as AddRelacionClienteInmobiliarioInmueble).IdClienteInmobiliario = idCliente;
  }
};

const saveOrEdit = async () => {
  if (isValid()) {
    try {
      // Primero guardamos el cliente
      const clienteGuardado = await formCliente.value!.saveOrEdit();
      
      if (!clienteGuardado) {
        return false;
      }
      
      if (selectedInmuebleId.value) {
        let modelToSend = model.value as AddRelacionClienteInmobiliarioInmueble;
        modelToSend.TipoRelacion = tipoRelacion.value;
        await api.callStandardApi("addRelacionClienteInmobiliarioInmueble", { showLoading: true }, selectedInmuebleId.value, modelToSend);
      }

      return true;
    } catch (error) {
      return false;
    }
  } else 
  {
    toastError(intl.i18n.t('clienteInmobiliario.validations.error'));
  }
  return false;
};

const validateFechaInicio = (e: { value: Date | null }) => {
  if (!e.value || !model.value.FechaFin) return true;
  const fechaInicio = new Date(e.value).setHours(0, 0, 0, 0);
  const fechaFin = new Date(model.value.FechaFin).setHours(0, 0, 0, 0);
  return fechaInicio <= fechaFin;
};

const validateFechaFin = (e: { value: Date | null }) => {
  if (!e.value || !model.value.FechaInicio) return true;
  const fechaFin = new Date(e.value).setHours(0, 0, 0, 0);
  const fechaInicio = new Date(model.value.FechaInicio).setHours(0, 0, 0, 0);
  return fechaFin >= fechaInicio;
};

watch(
  () => ({
    fechaInicio: model.value.FechaInicio,
    fechaFin: model.value.FechaFin
  }),
  () => {
    validationEngine.validateGroup("validacionFechaInicio");
    validationEngine.validateGroup("validacionFechaFin");
  }
);

const onInmuebleSelected = (inmueble: any) => {
  selectedInmuebleId.value = inmueble.Id;
  inmueblesDataSource.value = [inmueble];
  gridBoxOpened.value = false;
};

defineExpose({
  isValid,
  saveOrEdit
});
</script>
